// Personen-Suche für die Parteien-Karten des Übergabeprotokolls: EIN Suchbegriff,
// drei Quellen (mieter/eigentuemer/kontakte) parallel; die Mapper sind pur und
// vitest-getestet, die Supabase-Aufrufe bleiben dünn. Adress-Logik wie beim
// Signatur-Bezug (vorbelegung.ts): Mieter wohnen an der Objektadresse (+ Einheit).
import { supabase } from '../supabase/client';

export interface PersonTreffer {
  quelle: 'mieter' | 'eigentuemer' | 'kontakt';
  id: string;
  name: string;
  anschrift: string;
  email: string;
  telefon: string;
}

const s = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const adresse = (strasse: string, ort: string): string => [strasse, ort].filter(Boolean).join(', ');

export function mieterZuTreffer(m: any): PersonTreffer {
  const o = m?.objekt;
  const strasse = o ? `${s(o.strasse)} ${s(o.hausnummer)}`.trim() : '';
  const ort = o ? `${s(o.plz)} ${s(o.stadt)}`.trim() : '';
  const basis = adresse(strasse, ort);
  return {
    quelle: 'mieter', id: m.id,
    // Gewerbe-Mieter (firma_name gesetzt, kein Vor-/Nachname) gewinnen als Name —
    // Konvention wie bei eigentuemer/kontakt (s. lib/uebergabe/bezug.ts::ausMieter).
    name: s(m.firma_name) || `${s(m.vorname)} ${s(m.nachname)}`.trim(),
    anschrift: basis ? (s(m.einheit) ? `${basis}, ${s(m.einheit)}` : basis) : '',
    email: s(m.email), telefon: s(m.telefon),
  };
}

export function eigentuemerZuTreffer(e: any): PersonTreffer {
  return {
    quelle: 'eigentuemer', id: e.id,
    name: s(e.firma_name) || `${s(e.vorname)} ${s(e.nachname)}`.trim(),
    // hausnummer liegt in eigener Spalte (nicht Teil von strasse) — s. lib/uebergabe/bezug.ts::ladeEigentuemerKontakt.
    anschrift: adresse(`${s(e.strasse)} ${s(e.hausnummer)}`.trim(), `${s(e.plz)} ${s(e.stadt)}`.trim()),
    email: s(e.email), telefon: s(e.telefon),
  };
}

export function kontaktZuTreffer(k: any): PersonTreffer {
  return {
    quelle: 'kontakt', id: k.id,
    name: s(k.firma_name) || `${s(k.vorname)} ${s(k.nachname)}`.trim(),
    anschrift: adresse(`${s(k.strasse)} ${s(k.hausnummer)}`.trim(), `${s(k.plz)} ${s(k.stadt)}`.trim()),
    email: s(k.email), telefon: s(k.handynummer) || s(k.festnetz),
  };
}

/**
 * PostgREST-Struktur-Zeichen aus dem Suchbegriff entfernen: `,` trennt im
 * .or()-Filterstring Bedingungen, `()` gruppieren — unescaped könnte ein
 * Suchbegriff also eigene Filter injizieren (RLS begrenzt den Schaden, aber
 * die Struktur bleibt manipulierbar). Für eine Namenssuche ist Ersetzen durch
 * Leerzeichen verlustfrei genug.
 */
export function bereinigeSuchbegriff(query: string): string {
  return query.replace(/[,()]/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Sucht in Mietern, Eigentümern und Kontakten (je max. 5, Reihenfolge stabil). */
export async function suchePersonen(query: string): Promise<PersonTreffer[]> {
  const q = bereinigeSuchbegriff(query);
  if (q.length < 2) return [];
  const like = `%${q}%`;
  const [mieterQ, eigQ, kontaktQ] = await Promise.all([
    supabase.from('mieter')
      .select('id, vorname, nachname, firma_name, email, telefon, einheit, objekt:objekt_id(strasse, hausnummer, plz, stadt)')
      .or(`vorname.ilike.${like},nachname.ilike.${like},firma_name.ilike.${like}`).limit(5),
    supabase.from('eigentuemer')
      .select('id, vorname, nachname, firma_name, email, telefon, strasse, hausnummer, plz, stadt')
      .or(`vorname.ilike.${like},nachname.ilike.${like},firma_name.ilike.${like}`).limit(5),
    supabase.from('kontakte')
      .select('id, vorname, nachname, firma_name, email, handynummer, festnetz, strasse, hausnummer, plz, stadt')
      .or(`vorname.ilike.${like},nachname.ilike.${like},firma_name.ilike.${like}`).limit(5),
  ]);
  return [
    ...((mieterQ.data as any[]) || []).map(mieterZuTreffer),
    ...((eigQ.data as any[]) || []).map(eigentuemerZuTreffer),
    ...((kontaktQ.data as any[]) || []).map(kontaktZuTreffer),
  ];
}
